import { useState, useEffect, useRef, useCallback } from 'react';
import { optimizeSvg } from '../utils/svgOptimizer';

interface UseSvgPreloadOptions {
  onLoad?: () => void;
  onError?: (error: Error) => void;
  cacheKey?: string;
}

export const useSvgPreload = (src: string, options: UseSvgPreloadOptions = {}) => {
  const [svgContent, setSvgContent] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  // Store options in refs to avoid dependency issues
  const onLoadRef = useRef(options.onLoad);
  const onErrorRef = useRef(options.onError);
  const cacheKeyRef = useRef(options.cacheKey);
  
  // Update refs when options change
  useEffect(() => {
    onLoadRef.current = options.onLoad;
    onErrorRef.current = options.onError;
    cacheKeyRef.current = options.cacheKey;
  });

  const fetchSvg = useCallback(async (svgSrc: string, key: string) => {
    try {
      const response = await fetch(svgSrc);
      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.status}`);
      }
      const svgText = await response.text();
      
      // Optimize the SVG content
      const optimizedSvg = optimizeSvg(svgText);
      
      // Cache the optimized SVG content
      sessionStorage.setItem(`svg_${key}`, optimizedSvg);
      
      setSvgContent(optimizedSvg);
      setIsLoaded(true);
      setHasError(false);
      onLoadRef.current?.();
    } catch (error) {
      console.error('Error loading SVG:', error);
      setHasError(true);
      setIsLoaded(false);
      onErrorRef.current?.(error as Error);
    }
  }, []);

  useEffect(() => {
    if (!src) return;

    // Check if SVG is already cached
    const cacheKey = cacheKeyRef.current || src;
    const cachedSvg = sessionStorage.getItem(`svg_${cacheKey}`);
    
    if (cachedSvg) {
      setSvgContent(cachedSvg);
      setIsLoaded(true);
      setHasError(false);
      onLoadRef.current?.();
      return;
    }

    fetchSvg(src, cacheKey);
  }, [src, fetchSvg]);

  return { svgContent, isLoaded, hasError };
};
